import { observer } from 'mobx-react';
import { constantCase } from 'change-case';
import { ButtonGroup, GButton } from '@monorepo/base/src/components/buttons/group/button-group';
import { DropdownNew } from '@monorepo/base/src/components/dropdown-new/dropdown-new';
import { TableLink } from '@monorepo/base/src/components/table/table-link/table-link';
import { useStores } from '@monorepo/controlled/src/hooks/use-stores';
import { DeviceTypes } from '@monorepo/tools/src/lib/enums/device-types';
import { DataAttribute } from '@monorepo/tools/src/lib/models/data-attr.model';
import { ITrafficEntityError, ITrafficEntityFields } from '../../../apis/bidder.api';
import { IBidderInputOptionsGroup } from '../../../models/bidder-input-options.model';
import { parseRelatedCampaign } from '../../../models/traffic-entity.model';
import routes from '../../../routes/index.routes';
import { IAdminxStore } from '../../../stores';
import styles from './bidder-traffic.form.module.scss';

export const emptyTrafficFields = (): ITrafficEntityFields => ({
	geo: '',
	op_system: '',
	browser: '',
	device_type: '',
});

const fieldByApiName: Record<string, keyof ITrafficEntityFields> = {
	'traffic.geo': 'geo',
	'traffic.op_system': 'op_system',
	'traffic.browser': 'browser',
	'traffic.device_type': 'device_type',
};

export const splitTrafficErrors = (errors: ITrafficEntityError[] = []) => {
	const fieldErrors: Partial<Record<keyof ITrafficEntityFields, string>> = {};
	const messages: string[] = [];

	errors.forEach(error => {
		const key = fieldByApiName[error.field];
		if (key) {
			fieldErrors[key] = error.message;
		} else {
			messages.push(error.message);
		}
	});

	return { fieldErrors, messages };
};

export const missingTrafficFields = (fields: ITrafficEntityFields) => {
	const fieldErrors: Partial<Record<keyof ITrafficEntityFields, string>> = {};
	(Object.keys(fields) as (keyof ITrafficEntityFields)[]).forEach(key => {
		if (!fields[key]) {
			fieldErrors[key] = 'Empty field';
		}
	});
	return fieldErrors;
};

const dropdownClassNames = {
	dropdownWrapper: styles.w100,
	dropdownInput: styles.w100,
	autocompleteInputWrapper: styles.w100,
};

const toOptions = (groups: IBidderInputOptionsGroup[], withCode: boolean, onlyTargetable = false) => {
	const options = (onlyTargetable ? groups.filter(group => Boolean(group.to_target)) : groups).map(group => ({
		value: group.code,
		label: withCode ? `${group.name} - (${group.code})` : group.name,
	}));

	return options;
};

interface ITrafficForm {
	fields: ITrafficEntityFields;
	onChange: (fields: ITrafficEntityFields) => void;
	fieldErrors: Partial<Record<keyof ITrafficEntityFields, string>>;
	onClearError: (field: keyof ITrafficEntityFields) => void;
	relatedCampaigns?: string[];
}

export const TrafficForm = observer((props: ITrafficForm) => {
	const { fields, onChange, fieldErrors, onClearError, relatedCampaigns } = props;
	const { bidderStore } = useStores<IAdminxStore>();
	const inputOptions = bidderStore.getInputOptions();

	const setField = (key: keyof ITrafficEntityFields, values: (string | number)[]) => {
		onChange({ ...fields, [key]: values.filter(Boolean).join(',') });
		onClearError(key);
	};

	return (
		<div className={styles.form}>
			<ButtonGroup
				multi
				required
				title="Device Type"
				defaultValues={new Set(fields.device_type ? fields.device_type.split(',').filter(Boolean) : [])}
				onChange={values => setField('device_type', Array.from(values))}
				error={fieldErrors.device_type}>
				<GButton
					icon={'phone-android'}
					text={DeviceTypes.Mobile}
					value={constantCase(DeviceTypes.Mobile)}
					debugProps={{ dataAttrs: [new DataAttribute('id', 'device_types_mobile')] }}
				/>
				<GButton
					icon={'tablet-02'}
					text={DeviceTypes.Tablet}
					value={constantCase(DeviceTypes.Tablet)}
					debugProps={{ dataAttrs: [new DataAttribute('id', 'device_types_tablet')] }}
				/>
				<GButton
					icon={'monitor-01'}
					text={DeviceTypes.Computer}
					value={constantCase(DeviceTypes.Computer)}
					debugProps={{ dataAttrs: [new DataAttribute('id', 'device_types_computer')] }}
				/>
			</ButtonGroup>

			<DropdownNew
				classNames={dropdownClassNames}
				defaultValues={fields.op_system ? fields.op_system.split(',') : []}
				label="OS"
				required
				multi
				options={toOptions(inputOptions.getOperatingSystems(), false, true)}
				onSelect={values => setField('op_system', values)}
				error={fieldErrors.op_system}
				debugProps={{ dataAttrs: [new DataAttribute('id', 'os')] }}
			/>

			<DropdownNew
				classNames={dropdownClassNames}
				defaultValues={fields.browser ? fields.browser.split(',') : []}
				label="Browsers"
				required
				multi
				options={toOptions(inputOptions.getBrowsers(), false, true)}
				onSelect={values => setField('browser', values)}
				error={fieldErrors.browser}
				debugProps={{ dataAttrs: [new DataAttribute('id', 'browser')] }}
			/>

			<DropdownNew
				classNames={dropdownClassNames}
				required
				multi
				defaultValues={fields.geo ? fields.geo.split(',') : []}
				label="Geo"
				autocomplete
				autocompletePlaceholder="Search Country"
				options={toOptions(inputOptions.getGeos(), true)}
				onSelect={values => setField('geo', values)}
				error={fieldErrors.geo}
				debugProps={{ dataAttrs: [new DataAttribute('id', 'country_code')] }}
			/>

			{relatedCampaigns ? (
				<div className={styles.relatedCampaigns}>
					<div className={styles.relatedTitle}>Related Campaigns</div>
					{relatedCampaigns.length === 0 ? (
						<span>—</span>
					) : (
						<div className={styles.relatedList}>
							{relatedCampaigns.map((entry, index) => {
								const campaign = parseRelatedCampaign(entry);
								if (!campaign.id) {
									return <div key={`${entry}-${index}`}>{campaign.name}</div>;
								}
								return (
									<TableLink key={`${entry}-${index}`} to={routes.bidder.editCampaign(campaign.id)}>
										{`${campaign.id} - ${campaign.name}`}
									</TableLink>
								);
							})}
						</div>
					)}
				</div>
			) : null}
		</div>
	);
});
